// web/src/screens/preflight.ts — UI-D-14, PRIV-03.
//
// Parent-facing gate: explains mic usage, shows "Sākam" CTA.
// On tap → acquireMic() → if OK navigate to /intro/<gameShortCode>.
// On error (NotAllowedError / NotFoundError / AbortError) → /permission-denied.
//
// PRIV-03: mic is acquired only after an explicit parent tap — never speculatively.
// The OS permission sheet fires at the moment we await acquireMic().

import { acquireMic } from "../components/mic.js";
import { warmStreamingWorklet } from "../api/turn_stream.js";
import { navigate } from "../main.js";
import { getConsentStatus, acceptConsent } from "../api/consent.js";

async function micAlreadyGranted(): Promise<boolean> {
  // Safari historically threw on the "microphone" name; treat any failure
  // or non-granted state as "not granted" and fall through to the UI.
  try {
    const status = await navigator.permissions.query({ name: "microphone" as PermissionName });
    return status.state === "granted";
  } catch {
    return false;
  }
}

export function mount(root: HTMLElement, _props?: unknown): () => void {
  const sess = JSON.parse(sessionStorage.getItem("currentSession") ?? "{}") as {
    gameShortCode?: string;
  };
  const gameShortCode: string = sess.gameShortCode ?? "vocab";

  let cancelled = false;
  let cleanup: () => void = () => { cancelled = true; };

  void (async () => {
    if (await micAlreadyGranted()) {
      if (cancelled) return;
      try {
        const status = await getConsentStatus();
        if (cancelled) return;
        if (!status.accepted) await acceptConsent(status.text_hash);
        if (cancelled) return;
        await acquireMic();
        if (cancelled) return;
        // Pre-load the PCM worklet so the first turn's streaming setup doesn't
        // pay the module fetch on the prompt→mic critical path. Best-effort.
        void warmStreamingWorklet();
        navigate(`/intro/${encodeURIComponent(gameShortCode)}`);
        return;
      } catch {
        if (cancelled) return;
        navigate("/permission-denied");
        return;
      }
    }
    if (cancelled) return;
    cleanup = renderPreflight(root, gameShortCode);
  })();

  return () => cleanup();
}

function renderPreflight(root: HTMLElement, gameShortCode: string): () => void {
  root.innerHTML = `
    <section
      class="screen-preflight"
      style="min-height: 100dvh; padding: var(--space-lg); background: var(--surface); display: flex; flex-direction: column; align-items: center; justify-content: center; gap: var(--space-xl);"
    >
      <h1 style="font-size: var(--type-heading); text-align: center; max-width: 520px; margin: 0;">
        Lai spēle varētu klausīties, mums vajag piekļuvi mikrofonam.
      </h1>
      <p style="font-size: var(--type-body); text-align: center; max-width: 520px; margin: 0; color: var(--text-primary);">
        Mikrofons klausās tikai tad, kad mikrafona fona aplītis iekrāsojas.
      </p>
      <button
        class="cta"
        style="background: var(--theme-color); color: var(--text-on-color); border: 0; border-radius: 12px; padding: var(--space-md) var(--space-2xl); font-size: var(--type-heading); font-weight: 600; min-height: 64px; cursor: pointer;"
      >Sākam</button>
      <p style="font-size: var(--type-body); color: var(--text-muted); text-align: center; max-width: 520px; margin: 0;">
        Tu varēsi to mainīt jebkurā brīdī iestatījumos.
      </p>
    </section>
  `;

  const cta = root.querySelector(".cta") as HTMLButtonElement;

  async function onTap() {
    cta.disabled = true;
    try {
      // Record parental consent before mic acquisition. The "Sākam" CTA
      // text + the explanation above is the consent surface; tapping it
      // implies acceptance of the embedded LV consent text. The middleware
      // `consent.RequireBeforeMic` then unblocks /api/sessions/:id/turns.
      const status = await getConsentStatus();
      if (!status.accepted) {
        await acceptConsent(status.text_hash);
      }
      await acquireMic(); // triggers OS sheet on iOS the moment we await
      // Pre-load the PCM worklet so the first turn's streaming setup doesn't
      // pay the module fetch on the prompt→mic critical path. Best-effort.
      void warmStreamingWorklet();
      navigate(`/intro/${encodeURIComponent(gameShortCode)}`);
    } catch (err) {
      // OQ-11 discriminator: NotFoundError = no hardware; NotAllowedError / dismissed = denied
      const e = err as DOMException;
      if (e.name === "NotFoundError") {
        // No mic hardware — route to denied screen for settings guidance
        navigate("/permission-denied");
      } else {
        // NotAllowedError / AbortError / dismissed → denied recovery
        navigate("/permission-denied");
      }
    }
  }

  cta.addEventListener("click", onTap);
  return () => cta.removeEventListener("click", onTap);
}
